import React, { useEffect, useState } from "react";
import { displayName } from "../utils/theme.js";
import { paper, serif, sans } from "./docTheme.js";
import MarkdownLite from "./MarkdownLite.jsx";
import ItemFields from "./ItemFields.jsx";
import { clearReviewOnEdit } from "./sectionDraft.js";
import ConfirmDialog from "../clients/ConfirmDialog.jsx";
import { hasSharePointLink } from "./publicView.js";

const btn = { padding: "6px 12px", borderRadius: 6, border: `1px solid ${paper.line}`, background: paper.card, color: paper.inkSoft, fontFamily: sans, fontSize: 13, cursor: "pointer" };
const primary = { ...btn, background: paper.accent, borderColor: paper.accent, color: "#fff", fontWeight: 600 };
const blankItem = () => ({ id: `new-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, headline: "", body: "", checked: false, decisionNeeded: false, owner: null, dueDate: null, needsReview: false, sources: [] });
const when = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");
const ownerLabel = (o, users) => {
  if (!o) return null;
  if (o.type === "sawtooth") return "Sawtooth";
  if (o.type === "client") return "Client";
  const u = (users || []).find((x) => x.id === o.userId);
  return u ? displayName(u) : "Sawtooth";
};
// Editor-only note under text that publicView will strip a SharePoint/OneDrive link from.
export const SpHint = ({ style }) => <div style={{ fontSize: 12, color: paper.muted, ...style }}>SharePoint link — hidden from the client</div>;
const move = (arr, i, d) => { const n = [...arr]; const j = i + d; if (j < 0 || j >= n.length) return n; [n[i], n[j]] = [n[j], n[i]]; return n; };
const SOURCE_LABEL = { task: "Task", objective: "Line of effort", proposal: "Proposal", note: "Internal note", meeting: "Last meeting", file: "File", email: "Email" };

export default function SectionBlock({ section, index, editable, users, onSave, onRemove, onMoveUp, onMoveDown, onToggleAgenda, onClearReview, onRegenerate, agendaInteractive = true, actionsDisabled = false }) {
  const [draft, setDraft] = useState(null); // { title, items } while editing
  // The version the draft started from. The parent keys this block by section id (not rev), so a
  // newer rev arriving mid-edit keeps the draft; saving from base.rev lets the server flag it (409).
  const [base, setBase] = useState(section);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null); // { msg, rev }: rev = the section version it describes
  const [conflict, setConflict] = useState(null); // server's current section
  const [stash, setStash] = useState(null); // user's unsaved draft after loading theirs
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmRegen, setConfirmRegen] = useState(false);
  // A view-mode error describes one version of this section; drop it once a newer one arrives. A
  // conflict error belongs to the server's version (the host adopts it), so that one stays put.
  useEffect(() => { if (!draft) setError((e) => (e && section.rev > e.rev ? null : e)); }, [section.rev]); // eslint-disable-line react-hooks/exhaustive-deps
  const agenda = section.kind === "agenda";

  const startEdit = () => { setBase(section); setDraft({ title: section.title, items: section.items.map((i) => ({ ...i })) }); setError(null); setConflict(null); setStash(null); };
  const cancel = () => { setDraft(null); setError(null); setConflict(null); setStash(null); };
  const save = async () => {
    setBusy(true); setError(null);
    const items = clearReviewOnEdit(base.items, draft.items);
    const r = await onSave({ title: draft.title, items }, base.rev);
    setBusy(false);
    if (r.ok) { setDraft(null); setConflict(null); setStash(null); return; }
    if (r.conflict) { setConflict(r.current); return; }
    setError({ msg: r.error || "Couldn’t save this section.", rev: base.rev });
  };
  // View-mode actions (delete, move, clear review, agenda check) report their result here.
  const report = (r) => {
    if (!r || r.ok) { setError(null); return; }
    setError(r.conflict
      ? { msg: `${r.current?.updatedByName || "Someone"} changed this section — updated to the latest; try again.`, rev: r.current?.rev ?? section.rev }
      : { msg: r.error || "Couldn’t save this section.", rev: section.rev });
  };
  const run = async (fn, ...args) => report(await fn(...args));
  const runRegen = async () => { setConfirmRegen(false); setBusy(true); try { report(await onRegenerate()); } finally { setBusy(false); } };
  const loadLatest = () => {
    setStash(draft);
    setBase(conflict);
    setDraft({ title: conflict.title, items: conflict.items.map((i) => ({ ...i })) });
    setConflict(null);
  };

  const heading = (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 16 }}>
        <span style={{ fontFamily: serif, fontSize: 22, color: paper.accent }}>{String(index).padStart(2, "0")}</span>
        <h2 style={{ margin: 0, fontFamily: serif, fontWeight: 600, fontSize: 30, color: paper.ink, flex: 1 }}>{section.title}</h2>
        {editable && !draft && (
          <div style={{ display: "flex", gap: 6 }}>
            {onMoveUp && <button type="button" style={btn} onClick={() => run(onMoveUp)} disabled={actionsDisabled} aria-label="Move section up">↑</button>}
            {onMoveDown && <button type="button" style={btn} onClick={() => run(onMoveDown)} disabled={actionsDisabled} aria-label="Move section down">↓</button>}
            {onRegenerate && (
              <button type="button" style={btn} disabled={busy || actionsDisabled} onClick={() => (section.items.length ? setConfirmRegen(true) : runRegen())}>Regenerate</button>
            )}
            <button type="button" style={btn} onClick={startEdit}>Edit section</button>
            <button type="button" style={{ ...btn, color: paper.danger }} onClick={() => setConfirmDelete(true)} disabled={actionsDisabled}>Delete</button>
          </div>
        )}
      </div>
      {editable && !draft && hasSharePointLink(section.title) && <SpHint style={{ marginTop: 4 }} />}
    </div>
  );

  // A meeting can become read-only mid-edit (sent from another tab) — drop to view mode then.
  if (draft && editable) {
    return (
      <section style={{ padding: "40px 0", borderBottom: `1px solid ${paper.line}`, display: "flex", flexDirection: "column", gap: 14 }}>
        {heading}
        {conflict && (
          <div role="alert" style={{ background: paper.warnBg, border: `1px solid ${paper.warnLine}`, color: paper.warnInk, borderRadius: 8, padding: "10px 14px", fontSize: 14, display: "flex", gap: 12, alignItems: "center" }}>
            <span style={{ flex: 1 }}>{conflict.updatedByName || "Someone"} updated this section at {when(conflict.updatedAt)} — your changes weren’t saved.</span>
            <button type="button" style={btn} onClick={loadLatest}>Load latest version</button>
          </div>
        )}
        {error && <div role="alert" style={{ color: paper.danger, fontSize: 14 }}>{error.msg}</div>}
        <input aria-label="Section title" maxLength={120} value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })}
          style={{ fontFamily: serif, fontSize: 22, padding: "6px 10px", border: `1px solid ${paper.line}`, borderRadius: 6, color: paper.ink }} />
        {draft.items.map((it, i) => (
          <ItemFields key={it.id} item={it} agenda={agenda} users={users}
            onChange={(next) => setDraft({ ...draft, items: draft.items.map((x, j) => (j === i ? next : x)) })}
            onRemove={() => setDraft({ ...draft, items: draft.items.filter((_, j) => j !== i) })}
            onMoveUp={i > 0 ? () => setDraft({ ...draft, items: move(draft.items, i, -1) }) : undefined}
            onMoveDown={i < draft.items.length - 1 ? () => setDraft({ ...draft, items: move(draft.items, i, 1) }) : undefined} />
        ))}
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" style={btn} onClick={() => setDraft({ ...draft, items: [...draft.items, blankItem()] })}>+ Add item</button>
          <span style={{ flex: 1 }} />
          <button type="button" style={btn} onClick={cancel} disabled={busy}>Cancel</button>
          <button type="button" style={primary} onClick={save} disabled={busy || !draft.title.trim()}>{busy ? "Saving…" : "Save section"}</button>
        </div>
        {stash && (
          <div style={{ fontSize: 13, color: paper.muted, border: `1px dashed ${paper.line}`, borderRadius: 8, padding: 10 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Your unsaved text (copy anything you need):</div>
            {stash.items.map((i) => <div key={i.id} style={{ whiteSpace: "pre-wrap" }}>• {i.headline}{i.body ? `\n${i.body}` : ""}</div>)}
          </div>
        )}
      </section>
    );
  }

  return (
    <section style={{ padding: "40px 0", borderBottom: `1px solid ${paper.line}`, display: "flex", flexDirection: "column", gap: 16 }}>
      {heading}
      {error && <div role="alert" style={{ color: paper.danger, fontSize: 14 }}>{error.msg}</div>}
      {section.items.length === 0 && <div style={{ fontSize: 14, color: paper.muted }}>{editable ? "No items yet — use Edit section to add some." : ""}</div>}
      {agenda ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {section.items.map((i) => (
            <div key={i.id}>
              <label style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 16, color: i.checked ? paper.muted : paper.ink, textDecoration: i.checked ? "line-through" : "none" }}>
                <input type="checkbox" checked={i.checked} onChange={(e) => { if (agendaInteractive) run(onToggleAgenda, i.id, e.target.checked); }} disabled={!agendaInteractive} style={{ width: 18, height: 18, accentColor: paper.accent }} />
                <span style={{ flex: 1 }}>{i.headline}</span>
                {/* Agenda lines count toward "needs review" too, so they must show the badge. */}
                {editable && i.needsReview && (
                  <span style={{ display: "flex", alignItems: "center", gap: 6, textDecoration: "none" }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: "#fff", background: paper.danger, borderRadius: 4, padding: "3px 8px" }}>Review</span>
                    <button type="button" style={{ ...btn, padding: "2px 8px", fontSize: 12 }} onClick={(e) => { e.preventDefault(); run(onClearReview, i.id); }} disabled={actionsDisabled}>Clear</button>
                  </span>
                )}
              </label>
              {editable && hasSharePointLink(i.headline) && <SpHint style={{ marginLeft: 30 }} />}
            </div>
          ))}
        </div>
      ) : section.items.map((i) => {
        const owner = ownerLabel(i.owner, users);
        return (
          <article key={i.id} style={{ background: i.decisionNeeded ? paper.warnBg : paper.card, border: `1px solid ${i.decisionNeeded ? paper.warnLine : paper.line}`, borderRadius: 14, padding: "22px 28px", display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              {i.decisionNeeded && <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: paper.warnInk, background: "#fce0a8", borderRadius: 4, padding: "3px 8px" }}>Decision needed</span>}
              <span style={{ fontSize: 19, fontWeight: 700, color: paper.ink, flex: 1 }}>{i.headline}</span>
              {(owner || i.dueDate) && <span style={{ fontSize: 13, color: i.owner?.type === "client" ? paper.warnInk : paper.muted, fontWeight: 600 }}>{[owner, i.dueDate].filter(Boolean).join(" · ")}</span>}
              {editable && i.needsReview && (
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: "#fff", background: paper.danger, borderRadius: 4, padding: "3px 8px" }}>Review</span>
                  <button type="button" style={{ ...btn, padding: "2px 8px", fontSize: 12 }} onClick={() => run(onClearReview, i.id)} disabled={actionsDisabled}>Clear</button>
                </span>
              )}
            </div>
            <MarkdownLite text={i.body} style={{ fontSize: 16, lineHeight: 1.65, color: paper.inkSoft, maxWidth: 980 }} />
            {editable && hasSharePointLink(`${i.headline} ${i.body}`) && <SpHint />}
            {editable && (i.sources || []).length > 0 && (
              <details style={{ fontSize: 12, color: paper.muted }}>
                <summary style={{ cursor: "pointer" }}>Built from ({i.sources.length})</summary>
                <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                  {i.sources.map((s, k) => {
                    const kindLabel = SOURCE_LABEL[s.kind] || s.kind;
                    // Some saved labels (file, note, email) already carry the kind prefix baked
                    // in — don't double it up ("File: File: x.docx").
                    const kl = String(kindLabel).toLowerCase();
                    const already = typeof s.label === "string" && s.label.toLowerCase().startsWith(kl) && /^[:,]/.test(s.label.slice(kl.length));
                    const text = already ? s.label : `${kindLabel}: ${s.label}`;
                    return (
                      <li key={k}>
                        {s.url && s.url.startsWith("https://")
                          ? <a href={s.url} target="_blank" rel="noopener noreferrer">{text}</a>
                          : text}
                      </li>
                    );
                  })}
                </ul>
              </details>
            )}
          </article>
        );
      })}
      {editable && section.updatedByName && <div style={{ fontSize: 12, color: paper.muted }}>Edited by {section.updatedByName} · {when(section.updatedAt)}</div>}
      {confirmDelete && (
        <ConfirmDialog title="Delete section?" confirmLabel="Delete section"
          onCancel={() => setConfirmDelete(false)}
          onConfirm={() => { setConfirmDelete(false); run(onRemove); }}>
          “{section.title}” and its {section.items.length} item(s) will be deleted.
        </ConfirmDialog>
      )}
      {confirmRegen && (
        <ConfirmDialog title="Regenerate this section?" confirmLabel="Regenerate" onCancel={() => setConfirmRegen(false)} onConfirm={runRegen}>
          Its current items will be replaced with a new draft from the Ops Hub data.
        </ConfirmDialog>
      )}
    </section>
  );
}
